import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { Markdown } from "tiptap-markdown";
import { EditorToolbar } from "./EditorToolbar";
import { MermaidExtension } from "./extensions";
import { useEffect, useMemo } from "react";

// Helper to detect if content is Markdown
function isMarkdown(content: string): boolean {
  if (!content) return false;
  // Check for common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s/, // Headers
    /\*\*[^*]+\*\*/, // Bold
    /\*[^*]+\*/, // Italic
    /^\s*[-*+]\s/, // Unordered lists
    /^\s*\d+\.\s/, // Ordered lists
    /\[.+\]\(.+\)/, // Links
    /```/, // Code blocks
    /^>\s/, // Blockquotes
  ];
  return markdownPatterns.some((pattern) => pattern.test(content));
}

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export function RichTextEditor({
  content,
  onChange,
  placeholder = "Escribe el contenido del capitulo aqui...",
}: RichTextEditorProps) {
  // Memoize extensions to prevent recreation on each render
  // This prevents the "Duplicate extension names" warning in TipTap
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Highlight.configure({
        multicolor: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary-600 underline hover:text-primary-700",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-lg my-4",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Markdown.configure({
        html: true, // Allow HTML in markdown
        transformCopiedText: true, // Transform copied text to markdown
        transformPastedText: true, // Transform pasted markdown to HTML
      }),
      MermaidExtension,
    ],
    [placeholder],
  );

  const editor = useEditor({
    extensions,
    content: isMarkdown(content) ? content : content, // Markdown extension handles parsing
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose max-w-none min-h-[300px] p-4 focus:outline-none",
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when it changes externally (e.g., from AI generation)
  // Handles both Markdown and HTML content
  useEffect(() => {
    if (!editor) return;

    const currentHtml = editor.getHTML();
    // Only update if content actually changed
    if (content !== currentHtml) {
      // The Markdown extension will automatically parse markdown content
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  return (
    <div className="border border-secondary-300 rounded-lg overflow-hidden bg-white">
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}
